import React from 'react'
import { Link } from 'react-router-dom'
import SeoHead from '../components/SeoHead'
import { COMPANY, IMAGES } from '../data/siteData'
import { PAGE_META } from '../data/metaData'
import { getOptimizedImageUrl } from '../lib/imageHelper'
import CtaBanner from '../components/CtaBanner'

export default function AboutPage() {
  const metaTitle = PAGE_META.about.title
  const metaDescription = PAGE_META.about.description

  return (
    <div>
      <SeoHead
        title={metaTitle}
        description={metaDescription}
        canonicalPath="/om-jt-knudsen"
      />

      {/* Hero */}
      <section className="relative bg-stone-950 text-white py-16 sm:py-20 overflow-hidden border-b border-stone-800">
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedImageUrl(IMAGES.hero, { width: 1800, quality: 80 })}
            alt="JT Knudsen minigraver til haveanlæg og terrassearbejde"
            className="w-full h-full object-cover opacity-60 brightness-95"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950/80 via-stone-950/50 to-stone-950/20"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold uppercase tracking-wider mb-3">
              <i className="fa-solid fa-user-gear" aria-hidden="true"></i>
              <span>Fagmanden bag</span>
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight drop-shadow-md">
              Om JT Knudsen v/ Jonas Knudsen
            </h1>
            <p className="mt-4 text-base sm:text-xl text-stone-200 leading-relaxed drop-shadow-sm">
              En lokal anlægsvirksomhed med fokus på personlig kontakt, ordentligt håndværk og direkte dialog fra første henvendelse til det færdige resultat.
            </p>
          </div>
        </div>
      </section>

      {/* Main About Story */}
      <section className="py-16 sm:py-24 bg-stone-50 text-stone-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Image */}
            <div className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-stone-200">
                <img
                  src={getOptimizedImageUrl(IMAGES.jonas, { width: 800, height: 1000 })}
                  alt="Jonas Knudsen, indehaver af JT Knudsen - entreprenør i Hjortshøj"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute bottom-0 inset-x-0 bg-stone-950/85 backdrop-blur p-4 text-white">
                  <div className="font-bold text-base">Jonas Knudsen</div>
                  <div className="text-xs text-amber-400">Indehaver & maskinfører</div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-600">
                Personlig kontakt & ærligt arbejde
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-stone-900">
                Direkte kontakt til den person, der løser opgaven
              </h2>

              <div className="text-stone-700 space-y-4 text-base sm:text-lg leading-relaxed">
                <p>
                  <Link to="/" className="text-stone-900 font-semibold underline decoration-amber-500 decoration-2 underline-offset-2 hover:text-amber-600 transition-colors">JT Knudsen</Link> drives af Jonas Knudsen, som har base i Hjortshøj. Virksomheden udfører opgaver direkte for private boligejere og mindre virksomheder i hele lokalområdet, herunder Aarhus, Egå, Hornslet, Hinnerup, Grenaa og det øvrige Djursland.
                </p>
                <p>
                  Den største fordel ved at vælge en mindre, lokal entreprenør er overskueligheden: <strong>Kunden taler direkte med den person, der skal udføre arbejdet.</strong> Der er ingen fordyrende administrationsled, ingen misforståelser mellem sælger og håndværker, og fuld gennemsigtighed hele vejen igennem.
                </p>
                <p>
                  Vi har særligt fokus på mindre entreprenør-, grave- og anlægsopgaver – såsom nedgravning af trampoliner i parcelhushaver, etablering af terrasser og havebelægninger, udgravning til sokler og fundamenter samt maskingravning på trange arealer.
                </p>
              </div>

              <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-white border border-stone-200">
                  <div className="text-amber-600 font-bold text-base mb-1 flex items-center gap-2">
                    <i className="fa-solid fa-location-dot" aria-hidden="true"></i>
                    <span>Lokal base i Hjortshøj</span>
                  </div>
                  <p className="text-xs sm:text-sm text-stone-600">
                    Hurtig udrykning og lokalkendskab til jordbunden og forholdene i Østjylland og Djursland.
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-white border border-stone-200">
                  <div className="text-amber-600 font-bold text-base mb-1 flex items-center gap-2">
                    <i className="fa-solid fa-hand-holding-dollar" aria-hidden="true"></i>
                    <span>Uforpligtende tilbud</span>
                  </div>
                  <p className="text-xs sm:text-sm text-stone-600">
                    Gennemskuelige aftaler om tid, maskiner og jordhåndtering uden ubehagelige regninger.
                  </p>
                </div>
              </div>

              <div className="pt-4 flex flex-wrap items-center gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-stone-900 hover:bg-stone-800 text-white font-bold text-sm transition-colors"
                >
                  <span>Kontakt Jonas direkte</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </Link>

                <a
                  href={COMPANY.phoneHref}
                  className="inline-flex items-center gap-2 px-5 py-3.5 rounded-lg bg-white hover:bg-stone-100 text-stone-900 font-bold text-sm border border-stone-300 transition-colors whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone text-amber-500" aria-hidden="true"></i>
                  <span>Ring på {COMPANY.phone}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 sm:py-20 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-bold uppercase tracking-wider text-amber-400">
              Vores værdigrundlag
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mt-1">
              Hvad du kan forvente af et samarbejde
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-stone-950/60 p-6 rounded-xl border border-stone-800">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center text-lg mb-4">
                <i className="fa-solid fa-phone-volume" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">
                Tydelig kommunikation
              </h3>
              <p className="text-stone-400 text-sm leading-relaxed">
                Vi afstemmer forventningerne til punkt og prikke, så du ved præcis, hvad der sker hvornår.
              </p>
            </div>

            <div className="bg-stone-950/60 p-6 rounded-xl border border-stone-800">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center text-lg mb-4">
                <i className="fa-solid fa-broom" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">
                Pæn oprydning
              </h3>
              <p className="text-stone-400 text-sm leading-relaxed">
                Når maskinerne kører bort, efterlader vi altid grunden pæn, ryddelig og klar til brug for kunden.
              </p>
            </div>

            <div className="bg-stone-950/60 p-6 rounded-xl border border-stone-800">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center text-lg mb-4">
                <i className="fa-solid fa-shield-halved" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">
                Ordentligt forarbejde
              </h3>
              <p className="text-stone-400 text-sm leading-relaxed">
                Uanset om det gælder afretningslag under fliser, frostfri dybde til sokkel eller dræn til trampolin, gør vi arbejdet grundigt første gang.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <CtaBanner
        title="Vil du have en uforpligtende snak om dit projekt?"
        description="Jonas Knudsen er klar til at besvare dine spørgsmål og vurdere mulighederne på din adresse."
        primaryBtnText="Kontakt os nu"
        primaryBtnLink="/kontakt"
      />
    </div>
  )
}
